import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import {
  AppAutocomplete,
  AppButton,
  AppCheckbox,
  AppIconButton,
  AppTextField,
} from "../../../components/form";
import { PageSectionCard } from "../../../components/layout";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { pageSelectedRowSx } from "../../../lib/ui/formLayout";
import PrepScheduleCronField from "../../data-prep/components/PrepScheduleCronField";
import {
  adminDataAdaptorService,
  type DataAdaptor,
} from "../../../services/adminDataAdaptorService";
import {
  insightReportJobService,
  type InsightReportJob,
  type InsightReportJobPayload,
  type InsightReportJobRun,
  type InsightReportSourceType,
} from "../../../services/insightReportJobService";

function formatWhen(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function runStatusColor(status?: string): "default" | "success" | "warning" | "error" | "info" {
  switch (status) {
    case "SUCCEEDED":
      return "success";
    case "RUNNING":
    case "PENDING":
      return "warning";
    case "FAILED":
      return "error";
    default:
      return "default";
  }
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
        {value || "—"}
      </Typography>
    </Stack>
  );
}

type SourceTypeOption = { value: InsightReportSourceType; label: string };

const emptyForm = (): InsightReportJobPayload => ({
  name: "",
  description: "",
  sourceType: "OPENSEARCH",
  indexName: "",
  scheduleCron: "0 0 8 * * *",
  queryBody: "",
  recipientEmails: [],
  enabled: true,
});

export default function InsightReportJobsPage() {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const [jobs, setJobs] = useState<InsightReportJob[]>([]);
  const [adaptors, setAdaptors] = useState<DataAdaptor[]>([]);
  const [loading, setLoading] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuJob, setMenuJob] = useState<InsightReportJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<InsightReportJobPayload>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [recipients, setRecipients] = useState("");
  const [semanticText, setSemanticText] = useState("");
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [runs, setRuns] = useState<InsightReportJobRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<InsightReportJobRun | null>(null);

  const sourceTypeOptions = useMemo<SourceTypeOption[]>(
    () => [
      { value: "OPENSEARCH", label: t("widget.sourceOpenSearch") },
      { value: "ADAPTOR", label: t("widget.sourceAdaptor") },
    ],
    [t]
  );

  const selectedJob = useMemo(
    () => jobs.find((job) => job.id === selectedJobId) ?? null,
    [jobs, selectedJobId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [jobList, adaptorList] = await Promise.all([
        insightReportJobService.list(),
        adminDataAdaptorService.list(),
      ]);
      setJobs(jobList);
      setAdaptors((adaptorList || []).filter((a) => a.enabled && a.id));
      setSelectedJobId((current) => {
        if (current && jobList.some((job) => job.id === current)) return current;
        return jobList[0]?.id ?? null;
      });
    } catch {
      setError(t("insightJobs.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadRuns = useCallback(
    async (jobId: string) => {
      setRunsLoading(true);
      setRunsError(null);
      try {
        const page = await insightReportJobService.listRuns(jobId, 0, 50);
        setRuns(page?.content ?? []);
      } catch {
        setRuns([]);
        setRunsError(t("insightJobs.runsLoadError"));
      } finally {
        setRunsLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSelectedRun(null);
    if (!selectedJobId) {
      setRuns([]);
      setRunsError(null);
      return;
    }
    void loadRuns(selectedJobId);
  }, [selectedJobId, loadRuns]);

  const latestRun = runs[0] ?? null;

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setRecipients("");
    setSemanticText("");
    setDialogOpen(true);
  };

  const openEdit = (job: InsightReportJob) => {
    setEditingId(job.id);
    setForm({
      name: job.name,
      description: job.description || "",
      sourceType: job.sourceType,
      indexName: job.indexName || "",
      adaptorId: job.adaptorId,
      queryLanguage: job.queryLanguage,
      queryBody: job.queryBody || "",
      semanticQuery: job.semanticQuery ?? null,
      scheduleCron: job.scheduleCron,
      recipientEmails: job.recipientEmails || [],
      metricPath: job.metricPath,
      thresholdValue: job.thresholdValue,
      thresholdOperator: job.thresholdOperator,
      enabled: job.enabled,
    });
    setSemanticText(job.semanticQuery ? JSON.stringify(job.semanticQuery, null, 2) : "");
    setRecipients((job.recipientEmails || []).join(", "));
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      let semanticQuery: InsightReportJobPayload["semanticQuery"] = null;
      if (form.sourceType === "ADAPTOR" && semanticText.trim()) {
        try {
          semanticQuery = JSON.parse(semanticText);
        } catch {
          setError(t("insightJobs.semanticInvalid"));
          return;
        }
      }
      const payload: InsightReportJobPayload = {
        ...form,
        semanticQuery,
        recipientEmails: recipients
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (editingId) {
        await insightReportJobService.update(editingId, payload);
      } else {
        await insightReportJobService.create(payload);
      }
      setDialogOpen(false);
      await load();
    } catch {
      setError(t("insightJobs.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    const job = jobs.find((j) => j.id === id);
    try {
      await confirmDelete({ itemName: job?.name || id });
      await insightReportJobService.remove(id);
      if (selectedJobId === id) {
        setSelectedJobId(null);
        setRuns([]);
      }
      await load();
    } catch (error) {
      if (isCancelled(error)) return;
      setError(t("insightJobs.deleteError"));
    }
  };

  const jobToPayload = (job: InsightReportJob, enabled: boolean): InsightReportJobPayload => ({
    name: job.name,
    description: job.description || "",
    sourceType: job.sourceType,
    indexName: job.indexName || "",
    adaptorId: job.adaptorId,
    queryLanguage: job.queryLanguage,
    queryBody: job.queryBody || "",
    semanticQuery: job.semanticQuery ?? null,
    scheduleCron: job.scheduleCron,
    recipientEmails: job.recipientEmails || [],
    metricPath: job.metricPath,
    thresholdValue: job.thresholdValue,
    thresholdOperator: job.thresholdOperator,
    enabled,
  });

  const setJobEnabled = async (job: InsightReportJob, enabled: boolean) => {
    if (togglingId) return;
    setTogglingId(job.id);
    setError(null);
    const previous = job.enabled;
    setJobs((list) => list.map((item) => (item.id === job.id ? { ...item, enabled } : item)));
    try {
      await insightReportJobService.update(job.id, jobToPayload(job, enabled));
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            title: t("insightJobs.title"),
            content: enabled ? t("insightJobs.enabledToast") : t("insightJobs.disabledToast"),
          },
        })
      );
    } catch {
      setJobs((list) =>
        list.map((item) => (item.id === job.id ? { ...item, enabled: previous } : item))
      );
      setError(t("insightJobs.enableToggleError"));
    } finally {
      setTogglingId(null);
    }
  };

  const notifyRunResult = (
    titleKey: string,
    run: Awaited<ReturnType<typeof insightReportJobService.dryRun>>,
    successKey: string,
    mailSentKey: string,
    mailSkippedKey: string
  ) => {
    const mailNote =
      run.mailSent === true
        ? t(mailSentKey)
        : run.mailSkipReason
          ? t(mailSkippedKey, { reason: run.mailSkipReason })
          : t(successKey);
    window.dispatchEvent(
      new CustomEvent("showGlobalNotification", {
        detail: {
          color: run.mailSent === true ? "success" : "warning",
          icon: "check",
          title: t(titleKey),
          content: mailNote,
        },
      })
    );
  };

  const dryRun = async (id: string) => {
    try {
      const run = await insightReportJobService.dryRun(id);
      notifyRunResult(
        "insightJobs.dryRun",
        run,
        "insightJobs.dryRunSuccess",
        "insightJobs.dryRunMailSent",
        "insightJobs.dryRunMailSkipped"
      );
      setSelectedJobId(id);
      await load();
      await loadRuns(id);
    } catch {
      setError(t("insightJobs.dryRunError"));
    }
  };

  const runNow = async (id: string) => {
    try {
      const run = await insightReportJobService.runNow(id);
      notifyRunResult(
        "insightJobs.runNow",
        run,
        "insightJobs.runNowSuccess",
        "insightJobs.runNowMailSent",
        "insightJobs.runNowMailSkipped"
      );
      setSelectedJobId(id);
      await load();
      await loadRuns(id);
    } catch {
      setError(t("insightJobs.runNowError"));
    }
  };

  return (
    <PageListContainer>
      <PageHeader
        title={t("insightJobs.title")}
        subtitle={t("insightJobs.subtitle")}
        actions={
          <AppButton variant="contained" startIcon={<AddOutlinedIcon />} onClick={openCreate}>
            {t("insightJobs.create")}
          </AppButton>
        }
      />
      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}
      <PageSectionCard noPadding>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        ) : jobs.length === 0 ? (
          <PageEmptyState message={t("insightJobs.empty")} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("insightJobs.colName")}</TableCell>
                <TableCell>{t("insightJobs.colSource")}</TableCell>
                <TableCell>{t("insightJobs.colCron")}</TableCell>
                <TableCell>{t("insightJobs.colLastRun")}</TableCell>
                <TableCell>{t("insightJobs.colEnabled")}</TableCell>
                <TableCell align="right">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => {
                const isSelected = job.id === selectedJobId;
                const lastAt =
                  isSelected && latestRun
                    ? latestRun.finishedAt || latestRun.startedAt
                    : job.scheduleLastRunAt;
                return (
                  <TableRow
                    key={job.id}
                    hover
                    selected={isSelected}
                    onClick={() => setSelectedJobId(job.id)}
                    sx={{ ...pageSelectedRowSx, cursor: "pointer" }}
                  >
                    <TableCell>{job.name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={job.sourceType} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">
                        {job.scheduleCron}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{formatWhen(lastAt)}</Typography>
                        {isSelected && latestRun?.status && (
                          <Chip
                            size="small"
                            color={runStatusColor(latestRun.status)}
                            label={t(`insightJobs.status.${latestRun.status}`, {
                              defaultValue: latestRun.status,
                            })}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Switch
                        size="small"
                        checked={Boolean(job.enabled)}
                        disabled={togglingId === job.id}
                        onChange={(e) => void setJobEnabled(job, e.target.checked)}
                        inputProps={{ "aria-label": t("insightJobs.colEnabled") }}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                        <AppButton size="small" variant="contained" onClick={() => void runNow(job.id)}>
                          {t("insightJobs.runNow")}
                        </AppButton>
                        <AppIconButton
                          size="small"
                          title={t("common.actions")}
                          onClick={(e) => {
                            setMenuAnchor(e.currentTarget);
                            setMenuJob(job);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </AppIconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </PageSectionCard>

      {selectedJob && (
        <PageSectionCard>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
            >
              <Box>
                <Typography variant="h6">
                  {t("insightJobs.historyTitle", { name: selectedJob.name })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {latestRun
                    ? t("insightJobs.historyLatest", {
                        when: formatWhen(latestRun.finishedAt || latestRun.startedAt),
                        status: t(`insightJobs.status.${latestRun.status}`, {
                          defaultValue: latestRun.status,
                        }),
                      })
                    : t("insightJobs.historyNone")}
                </Typography>
              </Box>
              <AppButton
                size="small"
                variant="outlined"
                onClick={() => void loadRuns(selectedJob.id)}
                disabled={runsLoading}
              >
                {t("insightJobs.refreshHistory")}
              </AppButton>
            </Stack>
            {runsError && <Alert severity="error">{runsError}</Alert>}
            {runsLoading ? (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={24} />
              </Stack>
            ) : runs.length === 0 ? (
              <PageEmptyState message={t("insightJobs.historyEmpty")} />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("insightJobs.colStarted")}</TableCell>
                    <TableCell>{t("insightJobs.colFinished")}</TableCell>
                    <TableCell>{t("insightJobs.colStatus")}</TableCell>
                    <TableCell>{t("insightJobs.colTriggeredBy")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((run) => (
                    <TableRow
                      key={run.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSelectedRun(run)}
                    >
                      <TableCell>{formatWhen(run.startedAt)}</TableCell>
                      <TableCell>{formatWhen(run.finishedAt)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={runStatusColor(run.status)}
                          label={t(`insightJobs.status.${run.status}`, {
                            defaultValue: run.status,
                          })}
                        />
                      </TableCell>
                      <TableCell>{run.triggeredBy || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </PageSectionCard>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuJob)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuJob(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (!menuJob) return;
            const job = menuJob;
            setMenuAnchor(null);
            setMenuJob(null);
            void setJobEnabled(job, !job.enabled);
          }}
        >
          <ListItemIcon>
            <PowerSettingsNewOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {menuJob?.enabled ? t("insightJobs.disable") : t("insightJobs.enable")}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuJob) return;
            const id = menuJob.id;
            setMenuAnchor(null);
            setMenuJob(null);
            void dryRun(id);
          }}
        >
          <ListItemIcon>
            <ScienceOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("insightJobs.dryRun")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuJob) return;
            const job = menuJob;
            setMenuAnchor(null);
            setMenuJob(null);
            openEdit(job);
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.edit")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuJob) return;
            const id = menuJob.id;
            setMenuAnchor(null);
            setMenuJob(null);
            void remove(id);
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t("common.delete")}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(selectedRun)}
        onClose={() => setSelectedRun(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("insightJobs.runDetailTitle")}</DialogTitle>
        <DialogContent>
          {selectedRun && (
            <Stack spacing={2} mt={1}>
              <DetailField
                label={t("insightJobs.colStarted")}
                value={formatWhen(selectedRun.startedAt)}
              />
              <DetailField
                label={t("insightJobs.colFinished")}
                value={formatWhen(selectedRun.finishedAt)}
              />
              <DetailField
                label={t("insightJobs.colStatus")}
                value={t(`insightJobs.status.${selectedRun.status}`, {
                  defaultValue: selectedRun.status,
                })}
              />
              <DetailField
                label={t("insightJobs.colTriggeredBy")}
                value={selectedRun.triggeredBy || "—"}
              />
              <DetailField
                label={t("insightJobs.colSummary")}
                value={selectedRun.summary || "—"}
              />
              <DetailField
                label={t("insightJobs.fieldError")}
                value={selectedRun.error || "—"}
              />
              <DetailField
                label={t("insightJobs.fieldMetric")}
                value={
                  selectedRun.metricValue != null ? String(selectedRun.metricValue) : "—"
                }
              />
              <DetailField
                label={t("insightJobs.fieldThresholdBreached")}
                value={
                  selectedRun.thresholdBreached == null
                    ? "—"
                    : selectedRun.thresholdBreached
                      ? t("common.yes")
                      : t("common.no")
                }
              />
              <DetailField
                label={t("insightJobs.fieldMailSubject")}
                value={selectedRun.mailSubject || "—"}
              />
              <DetailField
                label={t("insightJobs.fieldMailSent")}
                value={
                  selectedRun.mailSent == null
                    ? "—"
                    : selectedRun.mailSent
                      ? t("common.yes")
                      : t("common.no")
                }
              />
              <DetailField
                label={t("insightJobs.fieldMailSkipReason")}
                value={selectedRun.mailSkipReason || "—"}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setSelectedRun(null)}>{t("common.close")}</AppButton>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingId ? t("insightJobs.editTitle") : t("insightJobs.createTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <AppTextField
              label={t("insightJobs.fieldName")}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <AppAutocomplete<SourceTypeOption>
              label={t("insightJobs.fieldSourceType")}
              options={sourceTypeOptions}
              value={
                sourceTypeOptions.find((o) => o.value === form.sourceType) ?? sourceTypeOptions[0]
              }
              disableClearable
              autoHighlight
              openOnFocus
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              onChange={(_, option) => {
                if (!option) return;
                setForm((f) => ({
                  ...f,
                  sourceType: option.value,
                  ...(option.value === "OPENSEARCH"
                    ? { adaptorId: undefined }
                    : { indexName: "" }),
                }));
              }}
            />
            {form.sourceType === "OPENSEARCH" ? (
              <AppTextField
                label={t("insightJobs.fieldIndex")}
                value={form.indexName || ""}
                onChange={(e) => setForm((f) => ({ ...f, indexName: e.target.value }))}
                fullWidth
              />
            ) : (
              <AppAutocomplete<DataAdaptor>
                label={t("insightJobs.fieldAdaptorId")}
                options={adaptors}
                value={adaptors.find((a) => a.id === form.adaptorId) ?? null}
                autoHighlight
                openOnFocus
                getOptionLabel={(a) => `${a.name} (${a.type})`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, option) =>
                  setForm((f) => ({
                    ...f,
                    adaptorId: option?.id,
                    queryLanguage: option?.type === "MONGODB" ? "MONGO" : "SQL",
                  }))
                }
              />
            )}
            <PrepScheduleCronField
              hideNone
              label={t("insightJobs.fieldCron")}
              helperText={t("insightJobs.fieldCronHelp")}
              value={form.scheduleCron}
              onChange={(scheduleCron) => setForm((f) => ({ ...f, scheduleCron }))}
            />
            {form.sourceType === "ADAPTOR" && form.queryLanguage !== "MONGO" && (
              <AppTextField
                label={t("insightJobs.fieldSemantic")}
                value={semanticText}
                onChange={(e) => setSemanticText(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                helperText={t("insightJobs.fieldSemanticHelp")}
                placeholder='{"metrics":["revenue"],"dimensions":["region"],"timeRange":{"last":"7d"}}'
                inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
              />
            )}
            <AppTextField
              label={t("insightJobs.fieldQuery")}
              value={form.queryBody || ""}
              onChange={(e) => setForm((f) => ({ ...f, queryBody: e.target.value }))}
              fullWidth
              multiline
              minRows={4}
              helperText={
                form.sourceType === "ADAPTOR" && form.queryLanguage === "MONGO"
                  ? t("insightJobs.fieldQueryMongoHelp")
                  : form.sourceType === "ADAPTOR"
                    ? t("insightJobs.fieldQuerySqlHelp")
                    : undefined
              }
              placeholder={
                form.sourceType === "ADAPTOR" && form.queryLanguage === "MONGO"
                  ? 'db.my_collection.find({}).sort({_id:-1}).limit(100)'
                  : form.sourceType === "ADAPTOR"
                    ? "SELECT * FROM my_table LIMIT 100"
                    : undefined
              }
            />
            <AppTextField
              label={t("insightJobs.fieldRecipients")}
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              fullWidth
              helperText={t("insightJobs.recipientsHint")}
            />
            <AppCheckbox
              label={t("insightJobs.fieldEnabled")}
              checked={Boolean(form.enabled)}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <AppButton variant="outlined" onClick={() => setDialogOpen(false)}>
            {t("common.cancel")}
          </AppButton>
          <AppButton onClick={() => void save()} disabled={saving}>
            {saving ? t("common.saving") : t("common.save")}
          </AppButton>
        </DialogActions>
      </Dialog>
    </PageListContainer>
  );
}
